import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Checkbox from "@material-ui/core/Checkbox";
import clsx from "clsx";
import AddIcon from "@material-ui/icons/Add";
import MoveIcon from "@material-ui/icons/MoveToInboxOutlined";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import DeleteIcon from "@material-ui/icons/DeleteOutline";
import CollectorIcon from "@material-ui/icons/SyncOutlined";
import AiIcon from "@material-ui/icons/EmojiObjectsOutlined";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PlayIcon from "@material-ui/icons/PlayArrowOutlined";
import PauseIcon from "@material-ui/icons/PauseOutlined";
import ResetIcon from "@material-ui/icons/Replay";

import { toast } from "react-toastify";
import { format } from "date-fns";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useProject } from "../../context/Project/ProjectContext";
import { useConfirm } from "../../components/ConfirmDialog";
import { useMenuStyles } from "../../components/menuStyles";
import CopilotPanel from "../Applications/CopilotPanel";
import { collectorProfile, makeCollectorRunTool } from "../Applications/collectorCopilot";

// Mesmo layout de card da tela de Automações (avatar + colunas rotuladas
// NOME/STATUS/USO/ATUALIZADO EM + status com bolinha + menu ⋮).
// paleta local (mesmos valores da tela de Automações — theme.app NÃO tem bg/panel/line/ink2/ink3)
const T = {
  bg: "#F6F7F9", panel: "#FFFFFF", line: "#E6E9ED", lineSoft: "#EEF0F3", hover: "#FAFBFC",
  ink: "#1B222A", ink2: "#5B6470", ink3: "#929AA4", ok: "#1E9E62", warn: "#C77A12",
  mono: 'ui-monospace,SFMono-Regular,"SF Mono",Menlo,Consolas,monospace',
};

const useStyles = makeStyles(theme => {
  return {
    page: { height: "100%", overflowY: "auto", background: T.bg },
    wrap: { width: "100%", maxWidth: 1100, margin: "0 auto", padding: theme.spacing(3.5, 3, 6) },
    header: { display: "flex", alignItems: "flex-start", gap: 12, marginBottom: theme.spacing(2.5) },
    headIcon: { width: 40, height: 40, borderRadius: 10, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#EEF0F3", color: "#5B6470", marginTop: 2 },
    title: { fontFamily: theme.app.heading, fontWeight: 700, fontSize: 22, color: T.ink },
    subtitle: { color: T.ink3, fontSize: 13, marginTop: 4, maxWidth: 720 },
    grow: { flexGrow: 1 },

    appList: { display: "flex", flexDirection: "column", gap: 10 },
    appCard: {
      display: "flex", alignItems: "center", gap: 14, background: T.panel,
      border: `1px solid ${T.line}`, borderRadius: 16, padding: "12px 16px", cursor: "pointer",
      transition: "box-shadow .15s ease, border-color .15s ease",
      "&:hover": { boxShadow: "0 4px 16px rgba(27,34,42,.06)" },
      "&:hover $kebab": { opacity: 1 },
    },
    appCardSel: { background: "#F4FBE0", boxShadow: "inset 2.5px 0 0 0 #CEFF00" },
    bulkBar: { display: "flex", alignItems: "center", gap: theme.spacing(1), padding: theme.spacing(0.5, 1.5), marginBottom: theme.spacing(1.5), borderRadius: 10, background: T.lineSoft, border: `1px solid ${T.line}` },
    selCtl: { display: "flex", alignItems: "center", gap: 0 },
    selCaret: { padding: 2, marginLeft: -6, color: T.ink3 },
    bulkHint: { color: T.ink3, fontSize: 12.5 },
    bulkCount: { fontWeight: 600, fontSize: 13, marginRight: theme.spacing(0.5), color: T.ink },
    appCardInactive: {
      background: T.hover || "#FAFBFC",
      boxShadow: "inset 3px 0 0 0 #9AA3AD",
      "& $appAvatar": { background: "#EEF0F3", color: "#9AA3AD" },
      "& $appName": { color: T.ink2 },
    },
    appAvatar: {
      width: 40, height: 40, borderRadius: 999, flexShrink: 0, background: "#EFF8D6", color: "#5f7a12",
      display: "flex", alignItems: "center", justifyContent: "center", "& svg": { fontSize: 20 },
    },
    appMain: { flex: 1, minWidth: 0 },
    nameRow: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    appName: { fontWeight: 600, fontSize: 14, color: T.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
    deactivatedPill: {
      display: "inline-flex", alignItems: "center", gap: 2, flexShrink: 0,
      fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".04em",
      color: "#5B6470", background: "#E7EAEE", border: "1px solid #D3D8DE",
      borderRadius: 999, padding: "1.5px 7px 1.5px 5px", lineHeight: 1.4,
      "& svg": { fontSize: 11 },
    },
    colStatus: { width: 150, flexShrink: 0, minWidth: 0 },
    colUse: { width: 96, flexShrink: 0, minWidth: 0 },
    colDate: { width: 150, flexShrink: 0 },
    colDateVal: { fontFamily: T.mono, fontSize: 12, color: T.ink3 },
    infoLabel: { fontSize: 10, textTransform: "uppercase", letterSpacing: ".05em", color: T.ink3, fontWeight: 600, marginBottom: 4 },
    status: { display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12.5, color: T.ink2 },
    dot: { width: 7, height: 7, borderRadius: "50%", flexShrink: 0 },
    ver: { fontFamily: T.mono, fontSize: 11, color: T.ink3, marginLeft: 1 },
    useChip: { fontSize: 11.5, fontWeight: 600, padding: "2px 9px", borderRadius: 999, whiteSpace: "nowrap", display: "inline-block" },
    useOn: { background: "#E6F4EC", color: T.ok },
    useOff: { background: T.lineSoft, color: T.ink3 },
    kebab: { opacity: 0.55, color: T.ink3, transition: "opacity .12s", "&:hover": { color: T.ink } },
    empty: { background: T.panel, border: `1px solid ${T.line}`, borderRadius: 16, padding: theme.spacing(7, 3), textAlign: "center", color: T.ink3, fontSize: 13.5, lineHeight: 1.6 },
  };
});

// status com bolinha (Publicado / Rascunho / Desativado) — igual ao das automações
const DOT = { ok: "#1E7E34", warn: "#B26A00", gray: "#9AA3AD" };

const CollectorsList = () => {
  const classes = useStyles();
  const menu = useMenuStyles();
  const history = useHistory();
  const { confirm } = useConfirm();
  const { activeProjectId, projects } = useProject();
  const [collectors, setCollectors] = useState([]);
  const [inUse, setInUse] = useState(new Set()); // ids de coletores ligados a alguma automação
  const [aiOpen, setAiOpen] = useState(false);
  const [rowMenu, setRowMenu] = useState(null); // { anchor, collector }
  const [selectedIds, setSelectedIds] = useState([]); // seleção múltipla (checkbox)
  const [projMenu, setProjMenu] = useState(null); // { anchor } — mover p/ projeto em massa
  const [selMenu, setSelMenu] = useState(null); // { anchor } — menu do cabeçalho (todos/nenhum/ativos/inativos)

  useEffect(() => { setSelectedIds([]); }, [activeProjectId]); // limpa seleção ao trocar de projeto

  const load = useCallback(async () => {
    if (!activeProjectId) return;
    try {
      const { data } = await api.get("/applications", { params: { projectId: activeProjectId } });
      const apps = data.applications || [];
      const cols = apps.filter(a => a.triggerType === "collector");
      // ativos primeiro, inativos depois; dentro de cada grupo, mais recente no topo
      cols.sort((a, b) => {
        const ia = a.active === false ? 1 : 0;
        const ib = b.active === false ? 1 : 0;
        if (ia !== ib) return ia - ib;
        return (b.updatedAt ? new Date(b.updatedAt).getTime() : 0) - (a.updatedAt ? new Date(a.updatedAt).getTime() : 0);
      });
      setCollectors(cols);
      // "em uso" = referenciado por alguma automação consumidora (gatilho data_collector)
      setInUse(new Set(
        apps
          .filter(a => a.triggerType === "data_collector")
          .map(a => Number((a.triggerConfig || {}).collectorId))
          .filter(Boolean)
      ));
    } catch (err) {
      toastError(err);
    }
  }, [activeProjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const aiRunTool = useCallback(
    makeCollectorRunTool(activeProjectId, data => {
      toast.success(`Coletor "${data.name}" criado! Feche o assistente para revisá-lo.`);
      load();
    }),
    [activeProjectId, load]
  );

  const create = () => {
    if (!activeProjectId) return;
    history.push(`/applications/new?projectId=${activeProjectId}&kind=collector`);
  };
  const open = c => history.push(`/applications/${c.code || c.id}?kind=collector`);
  const closeMenu = () => setRowMenu(null);

  // ativa/desativa o coletor (mesmo flag `active` das automações). Desativado: o
  // poller não agenda mais este coletor.
  const toggleActive = async c => {
    closeMenu();
    const next = c.active === false;
    try {
      await api.put(`/applications/${c.id}/active`, { active: next });
      toast.success(next ? `Coletor "${c.name}" ativado.` : `Coletor "${c.name}" desativado.`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // zera o CURSOR do coletor (estado de leitura). A próxima rodada relê do início
  // e REPROCESSA os registros mais recentes — logo REDISPARA as automações ligadas.
  const resetCursor = async c => {
    closeMenu();
    if (!(await confirm({
      title: "Resetar leitura do coletor",
      message: `Zerar o cursor de "${c.name}"? A próxima rodada vai reler desde o início e REPROCESSAR os registros mais recentes — isso REDISPARA as automações ligadas (pode reenviar/religar). O desenho do fluxo não muda.`,
      confirmText: "Resetar",
      danger: true,
    }))) return;
    try {
      const { data } = await api.post(`/applications/${c.id}/collector-reset`, {});
      toast.success(data && data.reset === false ? "Nada a resetar — o coletor ainda não leu nada." : `Leitura de "${c.name}" resetada.`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  const remove = async c => {
    closeMenu();
    if (!(await confirm({ title: "Remover coletor", message: `Remover o coletor "${c.name}"?`, confirmText: "Remover", danger: true }))) return;
    try {
      await api.delete(`/applications/${c.id}`);
      load();
    } catch (err) {
      toastError(err);
    }
  };

  // ---- seleção + operações em massa ----
  const sel = selectedIds.filter(id => collectors.some(c => c.id === id));
  const toggleOne = id => setSelectedIds(s => (s.includes(id) ? s.filter(x => x !== id) : [...s, id]));
  const clearSel = () => setSelectedIds([]);
  const allSel = collectors.length > 0 && sel.length === collectors.length;
  const someSel = sel.length > 0 && !allSel;
  const selectBy = kind => {
    setSelMenu(null);
    if (kind === "all") setSelectedIds(collectors.map(c => c.id));
    else if (kind === "active") setSelectedIds(collectors.filter(c => c.active !== false).map(c => c.id));
    else if (kind === "inactive") setSelectedIds(collectors.filter(c => c.active === false).map(c => c.id));
    else setSelectedIds([]);
  };

  const bulkSetActive = async active => {
    try {
      await Promise.all(sel.map(id => api.put(`/applications/${id}/active`, { active })));
      toast.success(`${sel.length} coletor(es) ${active ? "ativado(s)" : "desativado(s)"}.`);
      clearSel();
      load();
    } catch (err) { toastError(err); }
  };
  const bulkRun = async () => {
    if (!(await confirm({ title: "Executar agora", message: `Rodar ${sel.length} coletor(es) agora? Isso pode disparar as automações ligadas.`, confirmText: "Executar" }))) return;
    try {
      await Promise.all(sel.map(id => api.post(`/applications/${id}/collector-run-now`, {})));
      toast.success(`${sel.length} coletor(es) executado(s) — acompanhe em Execuções.`);
    } catch (err) { toastError(err); }
  };
  const bulkMoveToProject = async projectId => {
    setProjMenu(null);
    try {
      await Promise.all(sel.map(id => api.put(`/applications/${id}/project`, { projectId })));
      const p = (projects || []).find(x => x.id === projectId);
      toast.success(`${sel.length} movido(s) para "${(p && p.name) || "projeto"}".`);
      clearSel();
      load();
    } catch (err) { toastError(err); }
  };
  const bulkDelete = async () => {
    if (!(await confirm({ title: "Remover coletores", message: `Remover ${sel.length} coletor(es)? Não pode ser desfeito.`, confirmText: "Remover", danger: true }))) return;
    try {
      await Promise.all(sel.map(id => api.delete(`/applications/${id}`)));
      clearSel();
      load();
    } catch (err) { toastError(err); }
  };

  const renderStatus = c => {
    if (c.active === false) {
      return (
        <span className={classes.status}>
          <span className={classes.dot} style={{ background: DOT.gray }} />
          Desativado
          {c.publishedVersion ? <span className={classes.ver}>v{c.publishedVersion}</span> : null}
        </span>
      );
    }
    let color = DOT.gray;
    let label = "Rascunho";
    let ver = null;
    if (c.publishedVersion) {
      color = c.dirty ? DOT.warn : DOT.ok;
      label = c.dirty ? "Não publicado" : "Publicado";
      ver = `v${c.publishedVersion}`;
    }
    return (
      <span className={classes.status}>
        <span className={classes.dot} style={{ background: color }} />
        {label}
        {ver ? <span className={classes.ver}>{ver}</span> : null}
      </span>
    );
  };

  return (
    <div className={classes.page}>
      <div className={classes.wrap}>
        <div className={classes.header}>
          <div className={classes.headIcon}><CollectorIcon /></div>
          <div>
            <Typography className={classes.title}>Coletores de dados</Typography>
            <Typography className={classes.subtitle}>
              Verificam uma fonte (MySQL, HTTP…) no intervalo definido e trazem só os registros novos.
              Cada coletor é um fluxo — desenhado no mesmo canvas — que termina em "Retornar ao coletor".
            </Typography>
          </div>
          <div className={classes.grow} />
          <Button variant="outlined" color="primary" startIcon={<AiIcon />} onClick={() => setAiOpen(true)} disabled={!activeProjectId} style={{ marginRight: 8 }}>
            Criar com IA
          </Button>
          <Button variant="contained" color="primary" disableElevation startIcon={<AddIcon />} onClick={create} disabled={!activeProjectId}>
            Novo coletor
          </Button>
        </div>

        {collectors.length === 0 ? (
          <div className={classes.empty}>
            Nenhum coletor ainda.<br />
            Crie o primeiro para trazer dados novos e disparar automações (ex.: <b>clientes_novos</b>).
          </div>
        ) : (
          <>
            <div className={classes.bulkBar}>
                <span className={classes.selCtl}>
                  <Checkbox size="small" color="default" checked={allSel} indeterminate={someSel} onChange={() => setSelectedIds(allSel ? [] : collectors.map(c => c.id))} />
                  <IconButton size="small" className={classes.selCaret} onClick={e => setSelMenu({ anchor: e.currentTarget })}><ArrowDropDownIcon fontSize="small" /></IconButton>
                </span>
                {sel.length > 0 && (
                  <>
                    <span className={classes.bulkCount}>{sel.length} selecionado(s)</span>
                    <Button size="small" style={{ textTransform: "none" }} startIcon={<PlayIcon fontSize="small" />} onClick={() => bulkSetActive(true)}>Ativar</Button>
                    <Button size="small" style={{ textTransform: "none" }} startIcon={<PauseIcon fontSize="small" />} onClick={() => bulkSetActive(false)}>Desativar</Button>
                    <Button size="small" style={{ textTransform: "none" }} startIcon={<CollectorIcon fontSize="small" />} onClick={bulkRun}>Executar agora</Button>
                    <Button size="small" style={{ textTransform: "none" }} startIcon={<MoveIcon fontSize="small" />} onClick={e => setProjMenu({ anchor: e.currentTarget })} disabled={(projects || []).length < 2}>Mover para projeto…</Button>
                    <Button size="small" style={{ color: "#C0453B", textTransform: "none" }} startIcon={<DeleteIcon fontSize="small" />} onClick={bulkDelete}>Excluir</Button>
                    <div className={classes.grow} />
                    <Button size="small" style={{ textTransform: "none" }} onClick={clearSel}>Limpar</Button>
                  </>
                )}
              </div>
          <div className={classes.appList}>
            {collectors.map(c => {
              const used = inUse.has(c.id);
              const inactive = c.active === false;
              return (
                <div key={c.id} className={clsx(classes.appCard, selectedIds.includes(c.id) && classes.appCardSel, inactive && classes.appCardInactive)} onClick={() => open(c)}>
                  <span onClick={e => e.stopPropagation()} style={{ display: "flex" }}>
                    <Checkbox size="small" color="default" checked={selectedIds.includes(c.id)} onChange={() => toggleOne(c.id)} />
                  </span>
                  <div className={classes.appAvatar}><CollectorIcon /></div>

                  <div className={classes.appMain}>
                    <div className={classes.infoLabel}>Nome</div>
                    <div className={classes.nameRow}>
                      <span className={classes.appName}>{c.name}</span>
                      {inactive && <span className={classes.deactivatedPill}><PauseIcon />Desativado</span>}
                    </div>
                  </div>

                  <div className={classes.colStatus}>
                    <div className={classes.infoLabel}>Status</div>
                    {renderStatus(c)}
                  </div>

                  <div className={classes.colUse}>
                    <div className={classes.infoLabel}>Uso</div>
                    <span className={`${classes.useChip} ${used ? classes.useOn : classes.useOff}`}>{used ? "Em uso" : "Ocioso"}</span>
                  </div>

                  <div className={classes.colDate}>
                    <div className={classes.infoLabel}>Atualizado em</div>
                    <span className={classes.colDateVal}>{c.updatedAt ? format(new Date(c.updatedAt), "dd/MM/yyyy HH:mm") : "—"}</span>
                  </div>

                  <span onClick={e => e.stopPropagation()} style={{ display: "flex", marginLeft: "auto" }}>
                    <IconButton size="small" className={classes.kebab} onClick={e => setRowMenu({ anchor: e.currentTarget, collector: c })}>
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </span>
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      <Menu anchorEl={rowMenu ? rowMenu.anchor : null} open={!!rowMenu} onClose={closeMenu} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={() => rowMenu && toggleActive(rowMenu.collector)}>
          {rowMenu && rowMenu.collector.active === false ? <PlayIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
          {rowMenu && rowMenu.collector.active === false ? "Ativar" : "Desativar"}
        </MenuItem>
        <MenuItem className={menu.item} onClick={() => rowMenu && resetCursor(rowMenu.collector)}>
          <ResetIcon fontSize="small" />
          Resetar leitura (cursor)
        </MenuItem>
        <MenuItem className={menu.item} onClick={() => rowMenu && remove(rowMenu.collector)}>
          <DeleteIcon fontSize="small" />
          Remover coletor
        </MenuItem>
      </Menu>

      <Menu anchorEl={selMenu ? selMenu.anchor : null} open={!!selMenu} onClose={() => setSelMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        <MenuItem className={menu.item} onClick={() => selectBy("all")}>Todos</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("none")}>Nenhum</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("active")}>Somente ativos</MenuItem>
        <MenuItem className={menu.item} onClick={() => selectBy("inactive")}>Somente inativos</MenuItem>
      </Menu>

      <Menu anchorEl={projMenu ? projMenu.anchor : null} open={!!projMenu} onClose={() => setProjMenu(null)} PaperProps={{ className: menu.paper }} MenuListProps={{ disablePadding: true }}>
        {(projects || []).map(p => (
          <MenuItem key={p.id} className={menu.item} disabled={p.id === activeProjectId} onClick={() => bulkMoveToProject(p.id)}>
            {p.name}{p.id === activeProjectId ? " (atual)" : ""}
          </MenuItem>
        ))}
      </Menu>

      <CopilotPanel
        open={aiOpen}
        onClose={() => setAiOpen(false)}
        onOpen={() => setAiOpen(true)}
        runTool={aiRunTool}
        projectId={activeProjectId}
        appId="__collector__"
        profile={collectorProfile}
      />
    </div>
  );
};

export default CollectorsList;
